import React, { useEffect, useContext } from 'react';
import styled from 'styled-components';
import { NotebookText } from 'lucide-react';
import { useDrop } from 'react-dnd';
import { Takeaway, Note } from '@annote/types';
import { colors, text } from '@annote/core/styles';
import { usePanelContext } from 'src/context/PanelContext';
import { ShownIdsContext } from 'src/context/ShownContext';
import { FocusContext } from 'src/context/FocusContext';
import { useNote, useNotes } from 'src/context/noteCacheHooks';
import TakeawayCard from 'src/components/Cards/byType/takeaway/Takeaway';
import { useDndRef } from 'src/helpers/useDndRef';
import { PANEL_WIDTHS_PX } from 'src/constants/panelSizes';
import { Panel, PanelHeader } from './Panel';
import { PanelToggleButton } from './PanelToggleButton';

const WriterPanelWrapper = styled(Panel)`
  overflow: auto;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 1rem;
  padding: 2rem;
  color: ${colors.text.placeholder};
  text-align: center;

  svg {
    width: 48px;
    height: 48px;
  }
`;

const EmptyText = styled.div`
  font-family: ${text.family.primary};
  font-size: ${text.size.primary};
`;

const TakeawayContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  padding: 2rem;
  position: relative;
  box-sizing: border-box;
`;

const DropOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${colors.bg.dropzone};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: ${colors.text.primary};

  svg {
    width: 48px;
    height: 48px;
  }
`;

const isTakeaway = (note: Note | null | undefined): note is Takeaway => {
  return note?.type === 'takeaway';
};

interface WriterPanelProps {
  isOpen: boolean;
  offset?: number;
}

export const WriterPanel: React.FC<WriterPanelProps> = ({ isOpen, offset = 0 }) => {
  const { panels, setActiveId, openPanel, setPanelWidth, getPanelWidth, togglePanel } = usePanelContext();
  const { shownIds } = useContext(ShownIdsContext);
  const { focusedNoteId } = useContext(FocusContext);
  const { activeId } = panels.writer;
  const storedWidth = getPanelWidth('writer');

  // Reactive subscriptions - only re-renders when these specific notes change
  const activeTakeaway = useNote(activeId || undefined);
  const focusedNote = useNote(focusedNoteId || undefined);
  const shownNotes = useNotes(shownIds);

  // Drop zone for takeaways
  const [{ isOver, canDrop }, dropRef] = useDrop<Note, void, { isOver: boolean; canDrop: boolean }>({
    accept: ['TAKEAWAY'],
    drop: (item: Note) => {
      if (isTakeaway(item)) {
        setActiveId('writer', item.id);
        openPanel('writer', item.id);
      }
    },
    canDrop: (item) => isTakeaway(item),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const setDropRefWrapper = useDndRef(dropRef);

  // Auto-select a takeaway if no active ID is set
  // Priority: focused takeaway > first takeaway in shownIds > null
  useEffect(() => {
    if (activeId || !isOpen) return;

    // Priority 1: If focused note is a takeaway, use it
    if (focusedNote && isTakeaway(focusedNote) && focusedNoteId) {
      setActiveId('writer', focusedNoteId);
      return;
    }

    // Priority 2: First takeaway in shownIds
    const firstTakeaway = shownNotes.find((note) => note && isTakeaway(note));
    if (firstTakeaway) {
      setActiveId('writer', firstTakeaway.id);
    }
  }, [activeId, isOpen, focusedNote, focusedNoteId, shownNotes, setActiveId]);

  // Determine what to render
  const takeawayToRender = activeTakeaway && isTakeaway(activeTakeaway) ? activeTakeaway : null;
  const showDropOverlay = isOver && canDrop;

  return (
    <WriterPanelWrapper
      isOpen={isOpen}
      side="right"
      offset={offset}
      resizable={true}
      defaultWidth={storedWidth ?? PANEL_WIDTHS_PX.WRITER}
      minWidth={400}
      maxWidth={1000}
      onWidthChange={(width) => setPanelWidth('writer', width)}
    >
      <PanelHeader $side="left">
        <PanelToggleButton icon={NotebookText} onClick={() => togglePanel('writer')} title="Close Writer" />
      </PanelHeader>
      <div ref={setDropRefWrapper} style={{ height: '100%', position: 'relative' }}>
        {takeawayToRender ? (
          <TakeawayContainer>
            <TakeawayCard takeaway={takeawayToRender} readOnly={false} forceSize="LARGE" inPanel={true} />
            {showDropOverlay && (
              <DropOverlay>
                <NotebookText />
                <div>Drop to edit</div>
              </DropOverlay>
            )}
          </TakeawayContainer>
        ) : (
          <EmptyState>
            <NotebookText />
            <EmptyText>Drop a takeaway here to edit</EmptyText>
            {showDropOverlay && (
              <DropOverlay>
                <NotebookText />
                <div>Drop to edit</div>
              </DropOverlay>
            )}
          </EmptyState>
        )}
      </div>
    </WriterPanelWrapper>
  );
};
